import { useLocation, useNavigate } from "react-router-dom";
import React, { useEffect, useRef, useState } from "react";
import "./../styles/checkout.css";
import { useUser } from "../context/UserContext";
import ToastNotification, {
  showToast,
} from "../components/toasts/ToastNotification";
import { createNewOrder } from "../api/order.api";
import { ClipLoader } from "react-spinners";
import { applyDiscount } from "../api/discount.api";
import { getCart, removeCartItem } from "../api/cart.api";
import { nanoid } from "nanoid";

const Checkout = () => {
  const { user, setCart } = useUser();
  const [coupon, setCoupon] = useState("");
  const [discount, setDiscount] = useState(0);
  const [finalTotal, setFinalTotal] = useState(0);
  const [discountCode, setDiscountCode] = useState("");

  const [loadingDiscount, setLoadingDiscount] = useState(false);
  const [loading, setLoading] = useState(false);
  const { search } = useLocation();
  const params = new URLSearchParams(search);
  const location = useLocation();
  const state_data = location.state || {};

  const productId = params.get("productId");
  const product_type = params.get("product_type");

  const product = state_data.product || null;
  const product_package = state_data.product_package || null;
  const checkoutItemsFromState = Array.isArray(state_data.checkout_items)
    ? state_data.checkout_items
    : [];
  const isCheckoutFromCart = checkoutItemsFromState.length > 0;

  const navigate = useNavigate();
  const requestIdRef = useRef(`checkout_${nanoid()}`);

  useEffect(() => {
    document.title = 'Thanh toán đơn hàng';
  }, [productId]);

  const buildSingleCheckoutItem = () => {
    if (!productId || !product_type) return null;

    if (product_type === "topup_package") {
      return {
        productId,
        product_type,
        product_name: product_package?.name || product?.product_name,
        product_imgs: [product_package?.product_img || product?.product_imgs?.[0]],
        product_category: product?.product_category || "-",
        packageId: product_package?._id || null,
        selected_package: product_package
          ? {
              _id: product_package._id,
              name: product_package.name,
              price: product_package.price,
              status: product_package.status,
            }
          : null,
        product_attributes: product?.product_attributes || null,
      };
    }

    return {
      productId,
      product_type,
      product_name: product?.product_name || "-",
      product_imgs: product?.product_imgs || [],
      product_category: product?.product_category || "-",
      packageId: null,
      selected_package: null,
      product_attributes: product?.product_attributes || null,
    };
  };

  const checkoutItems = checkoutItemsFromState.length > 0
    ? checkoutItemsFromState.slice(0, 4)
    : [buildSingleCheckoutItem()].filter(Boolean);

  const subtotal = checkoutItems.reduce((sum, item) => {
    if (item?.product_type === "topup_package") {
      return sum + Number(item?.selected_package?.price || 0);
    }
    return sum + Number(item?.product_attributes?.price || 0);
  }, 0);

  useEffect(() => {
    const safeSubtotal = Number(subtotal || 0);
    const safeDiscount = Number(discount || 0);
    setFinalTotal(Math.max(safeSubtotal - safeDiscount, 0));
  }, [subtotal, discount]);

  useEffect(() => {
    if (checkoutItems.length > 1 && (discount > 0 || discountCode || coupon)) {
      setDiscount(0);
      setDiscountCode("");
      setCoupon("");
    }
  }, [checkoutItems.length, discount, discountCode, coupon]);

  const checkBalance = () => user?.balance - finalTotal >= 0;

  const handleApplyDiscount = async() => {
if (checkoutItems.length > 1) {
    showToast("Mã giảm giá chỉ áp dụng cho thanh toán 1 sản phẩm", "warning");
    return;
  }

  if (!coupon) {
    showToast("Vui lòng nhập mã giảm giá!", "warning");
    return;
  }

  try {
    setLoadingDiscount(true);

    const res = await applyDiscount(coupon, finalTotal);

    if (!res.success) {
      showToast(res.message || "Mã giảm giá không hợp lệ", "error");
      return;
    }

    const discountData = res.data;

    setDiscount(discountData.discountAmount);
    setFinalTotal(discountData.finalTotal)
    setDiscountCode(discountData.discountCode);

    showToast("Áp dụng mã giảm giá thành công!", "success");
  } catch (error) {
    showToast(error.message || "Lỗi áp dụng mã giảm giá", "error");
  } finally {
    setLoadingDiscount(false);
  }
  };

  const handleCreateOrder = async () => {
    if (loading) return;

    if (!checkoutItems.length) {
      showToast("Không có sản phẩm để thanh toán", "error");
      return;
    }

    setLoading(true);
    try {
      const isBatchCheckout = checkoutItems.length > 1;
      const payload = isBatchCheckout
        ? {
            requestId: requestIdRef.current,
            discountCode: "",
            amount: finalTotal,
            items: checkoutItems.map((item) => ({
              productId: item.productId,
              product_type: item.product_type,
              packageId: item.packageId || item?.selected_package?._id || null,
            })),
          }
        : {
            requestId: requestIdRef.current,
            discountCode,
            productId: checkoutItems[0]?.productId,
            product_type: checkoutItems[0]?.product_type,
            amount: finalTotal,
            packageId:
              checkoutItems[0]?.packageId ||
              checkoutItems[0]?.selected_package?._id ||
              null,
          };

      const res = await createNewOrder(payload);

      if (res?.success) {
        if (isCheckoutFromCart) {
          try {
            for (const item of checkoutItems) {
              await removeCartItem(
                item?.productId,
                item?.packageId || item?.selected_package?._id || null
              );
            }

            const cartRes = await getCart();
            if (cartRes?.success) {
              setCart(Number(cartRes?.data?.cart?.totalItems || 0));
            }
          } catch (clearCartError) {
            showToast(
              clearCartError?.message || "Thanh toán thành công nhưng chưa cập nhật giỏ hàng",
              "warning"
            );
          }
        }

        if (isBatchCheckout) {
          navigate("/order-success", {
            state: {
              isBatch: true,
              orderIds: res?.data?.orderIds || [],
            },
          });
        } else {
          navigate("/order-success", {
            state: {
              orderId: res.data.orderId,
              product_type: checkoutItems[0]?.product_type,
              order_attributes: res.data.item,
              isValuable: res.data.isValuable,
            },
          });
        }
      }
    } catch (error) {
      showToast(error.message, "error");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteDiscount = () => {
    setDiscount(0);
    setDiscountCode("");
    setCoupon("");
  }

  const getTypeLabel = (type) =>
    type === "topup_package"
      ? "Gói nạp"
      : type === "game_account"
      ? "Tài khoản game"
      : type === "utility_account"
      ? "Tài khoản tiện ích"
      : type === "gift_card"
      ? "Gift card"
      : "-";

  const getItemPrice = (item) => {
    if (!item) return 0;
    if (item.product_type === "topup_package") {
      return Number(item?.selected_package?.price || 0);
    }
    return Number(item?.product_attributes?.price || 0);
  };

  const formatMoney = (value) => `${Number(value || 0).toLocaleString("vi-VN")}đ`;

  return (
    <div className="checkout-wrapper">
      <ToastNotification />
      <div className="checkout-shell">
        <header className="checkout-header">
          <h1 className="checkout-title">Thanh toán đơn hàng</h1>
          <p className="checkout-subtitle">
            Kiểm tra thông tin đơn hàng, áp dụng mã giảm giá và xác nhận thanh toán.
          </p>
        </header>

        <div className="checkout-grid">
          <section className="checkout-card checkout-order-card">
            <h2 className="checkout-card-title">Đơn hàng</h2>
            <p className="checkout-order-desc">Bạn đang thanh toán {checkoutItems.length} sản phẩm.</p>

            <div className="checkout-order-list">
              {checkoutItems.map((item) => (
                <article
                  key={`${item?.productId}_${item?.packageId || item?.selected_package?._id || "default"}`}
                  className="checkout-order-item"
                >
                  <img
                    loading="lazy"
                    src={item?.product_imgs?.[0]}
                    className="checkout-item-img checkout-item-img-large"
                    alt={item?.product_name || "Checkout Item"}
                  />

                  <div className="checkout-order-meta">
                    <h3 className="checkout-order-name">{item?.product_name}</h3>
                    <div className="checkout-block checkout-product-details">
                      <div className="checkout-detail-row">
                        <span>Mã sản phẩm</span>
                        <strong>{item?.productId || "-"}</strong>
                      </div>
                      <div className="checkout-detail-row">
                        <span>Loại</span>
                        <strong>{getTypeLabel(item?.product_type)}</strong>
                      </div>
                      <div className="checkout-detail-row">
                        <span>Danh mục</span>
                        <strong>{item?.product_category || "-"}</strong>
                      </div>
                      {item?.selected_package?.name && (
                        <div className="checkout-detail-row">
                          <span>Gói nạp</span>
                          <strong>{item.selected_package.name}</strong>
                        </div>
                      )}
                      <div className="checkout-detail-row">
                        <span>Đơn giá</span>
                        <strong>{formatMoney(getItemPrice(item))}</strong>
                      </div>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          </section>

          <aside className="checkout-card checkout-summary-card">
            <h2 className="checkout-card-title">Tóm tắt đơn hàng</h2>

            <div className="checkout-block">
              <div className="checkout-block-head">
                <h3>Số dư ví</h3>
                <span className={`checkout-badge ${checkBalance() ? "is-good" : "is-bad"}`}>
                  {checkBalance() ? "Đủ số dư" : "Thiếu số dư"}
                </span>
              </div>
              <p className={`checkout-balance ${!checkBalance() ? "lack-balance" : ""}`}>
                Số dư hiện tại: <strong>{formatMoney(user?.balance)}</strong>
              </p>
              {!checkBalance() && (
                <button
                  type="button"
                  onClick={() => navigate("/payment")}
                  className="checkout-link-btn"
                >
                  Nạp thêm tiền
                </button>
              )}
            </div>

            <div className="checkout-block">
              <div className="checkout-block-head">
                <h3>Mã giảm giá</h3>
              </div>
              <div className="checkout-coupon-row">
                <input
                  type="text"
                  value={coupon}
                  onChange={(e) => setCoupon(e.target.value)}
                  placeholder="Nhập mã giảm giá..."
                  className="checkout-coupon-input"
                  disabled={checkoutItems.length > 1}
                />
                {discount > 0 ? (
                  <button
                    className="check-out-btn checkout-coupon-btn delete-discount-button"
                    onClick={handleDeleteDiscount}
                    disabled={loading}
                  >
                    Xóa mã
                  </button>
                ) : (
                  <button
                    className="check-out-btn checkout-coupon-btn"
                    onClick={handleApplyDiscount}
                    disabled={loading || checkoutItems.length > 1}
                  >
                    {loadingDiscount ? <ClipLoader size={18} color="#fff" /> : "Áp dụng"}
                  </button>
                )}
              </div>
              {checkoutItems.length > 1 && (
                <p className="discount-text">
                  Mã giảm giá chỉ hỗ trợ thanh toán 1 sản phẩm.
                </p>
              )}
              {discount > 0 && (
                <p className="discount-text">
                  Bạn đang sử dụng mã <span className="discount-code">{discountCode}</span>
                </p>
              )}
            </div>

            <div className="check-out-summary-line">
              <span>Số sản phẩm</span>
              <span>{checkoutItems.length}/4</span>
            </div>
            <div className="check-out-summary-line">
              <span>Tạm tính</span>
              <span>{formatMoney(subtotal)}</span>
            </div>
            <div className="check-out-summary-line">
              <span>Giảm giá</span>
              <span>-{formatMoney(discount)}</span>
            </div>

            <div className="check-out-summary-total">
              <strong>Tổng cần thanh toán</strong>
              <strong>{formatMoney(finalTotal)}</strong>
            </div>

            <button
              onClick={handleCreateOrder}
              disabled={loading || !checkBalance()}
              className={`check-out-btn check-out-pay-btn ${loading || !checkBalance() ? "disabled-button" : ""}`}
            >
              {loading ? <ClipLoader color="#fff" size={20} /> : "Thanh toán ngay"}
            </button>

            <p className="check-out-disclaimer">
              Bằng cách bấm <strong>Thanh toán</strong>, tôi xác nhận đã đọc
              <a href="/term-of-service" target="_blank" rel="noopener noreferrer"> Điều khoản dịch vụ</a>
              {" "}và
              <a href="/privacy-policy" target="_blank" rel="noopener noreferrer"> Chính sách bảo mật</a>.
            </p>
          </aside>
        </div>
      </div>
    </div>
  );
};

export default Checkout;
